import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

const navLinks = [
  { label: "Services", items: ["Shipping", "Warehousing", "Customs"] },
  { label: "About Us", items: ["Our Story", "Team", "Careers"] },
  { label: "Partnerships", items: ["Become a Partner", "Affiliates"] },
  { label: "Resources", items: ["Blog", "Help Center", "Guides"] },
];

const MainNavbar = () => {
  const [anchorEls, setAnchorEls] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const handleOpen = (event, label) => {
    setAnchorEls((prev) => ({ ...prev, [label]: event.currentTarget }));
  };

  const handleClose = (label) => {
    setAnchorEls((prev) => ({ ...prev, [label]: null }));
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        position: isSticky ? "fixed" : "absolute",
        top: 0,
        left: 0,
        zIndex: 3,
        width: "100%",
        transition: "all 0.3s ease",
        backgroundColor: isSticky ? "rgba(15, 23, 42, 0.5)" : "transparent",
        backdropFilter: isSticky ? "blur(12px)" : "none",
        "&::after": isSticky
          ? {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "2px",
              background:
                "linear-gradient(to right, rgba(255,255,255,0.2), rgba(255,255,255,0), rgba(255,255,255,0.2))",
              backdropFilter: "blur(8px)",
            }
          : {},
      }}
    >
      <Box
        sx={{
          maxWidth: "1440px",
          margin: "0 auto",
          px: { xs: "1rem", md: "7rem" },
          py: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "var(--nav-text)",
        }}
      >
        {/* Left: Logo + Desktop Nav */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: { xs: 2, md: 3 } }}
        >
          <img
            src="/assets/skymart-logo.png"
            alt="Skymart Logo"
            style={{ height: "3rem", objectFit: "contain", display: "none" }}
            className="desktop-logo"
          />
          <img
            src="/assets/skymart-logo.png"
            alt="Skymart Logo"
            style={{ height: "3rem", objectFit: "contain", display: "block" }}
            className="mobile-logo"
          />

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 3 }}>
            {navLinks.map(({ label, items }) => (
              <Box key={label}>
                <Button
                  onClick={(e) => handleOpen(e, label)}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    color: "var(--nav-text)",
                    textTransform: "none",
                    fontSize: "1rem",
                    "&:hover": {
                      color: "var(--nav-hover)",
                      background: "transparent",
                    },
                  }}
                >
                  {label}
                </Button>
                <Menu
                  anchorEl={anchorEls[label]}
                  open={Boolean(anchorEls[label])}
                  onClose={() => handleClose(label)}
                  MenuListProps={{ sx: { backgroundColor: "#0f172a" } }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  {items.map((item) => (
                    <MenuItem
                      key={item}
                      component={Link}
                      href="https://skymartexpress.com"
                      target="_blank"
                      onClick={() => handleClose(label)}
                      sx={{
                        color: "var(--nav-text)",
                        "&:hover": { backgroundColor: "#1e293b" },
                      }}
                    >
                      {item}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Desktop Actions */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 3,
          }}
        >
          <Button
            component={Link}
            href="https://skymartexpress.com"
            target="_blank"
            variant="contained"
            sx={{
              backgroundColor: "var(--yellow-main)",
              color: "#000",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "var(--yellow-hover)" },
              borderRadius: "8px",
              px: 3,
              py: 1,
              fontSize: "1rem",
            }}
          >
            Get Started
          </Button>
          <Typography
            component={Link}
            href="https://skymartexpress.com"
            target="_blank"
            sx={{
              color: "var(--nav-text)",
              textDecoration: "none",
              fontSize: "1rem",
              "&:hover": {
                color: "var(--nav-hover)",
                textDecoration: "underline",
              },
            }}
          >
            Français
          </Typography>
        </Box>

        {/* Mobile Hamburger */}
        <IconButton
          sx={{ color: "var(--nav-text)", display: { xs: "flex", md: "none" } }}
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon fontSize="large" />
        </IconButton>
      </Box>

      {/* Mobile Buttons under logo */}
      <Box
        sx={{
          display: { xs: isSticky ? "none" : "flex", md: "none" },
          justifyContent: "center",
          gap: "1rem",
          mt: "1rem",
          transition: "opacity 0.3s ease",
        }}
      >
        <Button
          component={Link}
          href="https://skymartexpress.com"
          target="_blank"
          variant="contained"
          sx={{
            backgroundColor: "var(--yellow-main)",
            color: "#000",
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1.2,
            fontSize: "1.1rem",
            borderRadius: "10px",
            "&:hover": { backgroundColor: "var(--yellow-hover)" },
          }}
        >
          Get Started
        </Button>
        <Button
          component={Link}
          href="https://skymartexpress.com"
          target="_blank"
          variant="outlined"
          sx={{
            color: "var(--nav-text)",
            borderColor: "var(--nav-text)",
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1.2,
            fontSize: "1.1rem",
            borderRadius: "10px",
            "&:hover": {
              borderColor: "var(--nav-hover)",
              color: "var(--nav-hover)",
            },
          }}
        >
          Français
        </Button>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: "75%",
            backgroundColor: "#0f172a",
            color: "var(--nav-text)",
            p: 2,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Menu</Typography>
          <IconButton
            sx={{ color: "var(--nav-text)" }}
            onClick={() => setDrawerOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

        <List>
          {navLinks.map(({ label, items }) => (
            <Box key={label} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, color: "white" }}>
                {label}
              </Typography>
              {items.map((item) => (
                <ListItem
                  key={item}
                  button
                  component={Link}
                  href="https://skymartexpress.com"
                  target="_blank"
                  sx={{
                    pl: 2,
                    py: 0.5,
                    "& .MuiTypography-root": { color: "white" }, // force white text
                    "&:hover": {
                      backgroundColor: "#1e293b",
                      "& .MuiTypography-root": { color: "white" }, // keep white on hover
                    },
                  }}
                >
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ sx: { color: "inherit" } }}
                  />
                </ListItem>
              ))}
            </Box>
          ))}
        </List>
      </Drawer>
    </Box>
  );
};

export default MainNavbar;
